import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Line } from 'recharts';

interface JuridicaNetIncomeData {
  general_table: any[];
  analysis_table: any[];
}

interface JuridicaNetIncomeViewProps {
  data: JuridicaNetIncomeData;
  title: string;
}

const formatYAxis = (tickItem: number) => {
  if (tickItem >= 1000000000) {
    return `${(tickItem / 1000000000).toFixed(1)}B`;
  }
  if (tickItem >= 1000000) {
    return `${(tickItem / 1000000).toFixed(1)}M`;
  }
  if (tickItem >= 1000) {
    return `${(tickItem / 1000).toFixed(1)}K`;
  }
  return tickItem.toString();
};

const JuridicaNetIncomeView: React.FC<JuridicaNetIncomeViewProps> = ({ data, title }) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (!data || !data.analysis_table || data.analysis_table.length === 0) {
    return (
      <Paper sx={{ p: 2, textAlign: 'center' }}>
        <Typography>No hay datos disponibles para {title}.</Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" gutterBottom>Análisis de Renta Líquida</Typography>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="sub-vistas de renta líquida">
          <Tab label="Gráfica" />
          <Tab label="Tabla General" />
          <Tab label="Tabla Renta Líquida" />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="h6">Total Renta Líquida con Tendencia</Typography>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={data.analysis_table}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="Anio" type="category" />
              <YAxis tickFormatter={formatYAxis} />
              <Tooltip formatter={(value: number) => new Intl.NumberFormat('es-CO').format(value)} />
              <Legend />
              <Bar dataKey="Total_Renta_liquida" fill={theme.palette.primary.main} name="Total Renta Líquida" />
              <Line type="monotone" dataKey="Total_Renta_liquida" stroke={theme.palette.secondary.main} name="Tendencia" />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {activeTab === 1 && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="h6">Tabla General</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Año</TableCell>
                  <TableCell>Total Renta Líquida</TableCell>
                  <TableCell>Análisis %</TableCell>
                  <TableCell>Análisis $</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.analysis_table.map((row: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell>{row.Anio}</TableCell>
                    <TableCell>{new Intl.NumberFormat('es-CO').format(row.Total_Renta_liquida)}</TableCell>
                    <TableCell>{row['Analisis %'] ? row['Analisis %'].toFixed(2) : '0.00'}%</TableCell>
                    <TableCell>{new Intl.NumberFormat('es-CO').format(row['Analisis $'])}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {activeTab === 2 && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="h6">Tabla de Total_Renta_liquida</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Año</TableCell>
                  <TableCell>Renta Exenta</TableCell>
                  <TableCell>Rentas Gravables</TableCell>
                  <TableCell>Total Renta Líquida</TableCell>
                  <TableCell>Total Renta Líquida Gravable</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.general_table.map((row: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell>{row.Anio}</TableCell>
                    <TableCell>{new Intl.NumberFormat('es-CO').format(row.Renta_exenta)}</TableCell>
                    <TableCell>{new Intl.NumberFormat('es-CO').format(row.Rentas_gravables)}</TableCell>
                    <TableCell>{new Intl.NumberFormat('es-CO').format(row.Total_Renta_liquida)}</TableCell>
                    <TableCell>{new Intl.NumberFormat('es-CO').format(row.Total_Renta_liquida_gravable)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default JuridicaNetIncomeView;